import App from "./App.tsx";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { selectAccessToken } from "./store/modules/users/users.selectors.ts";
import { useEffect, useMemo } from "react";
import { AppModalRegistry } from "./app-modal-registry.tsx";
import RootLayout from "./app/layout.tsx";
import { RouteGuard } from "./components/route-guard.tsx";
import AuthLayout from "./app/auth/layout.tsx";
import { HqAuthGuard } from "./components/hq/hq-auth-guard.tsx";
import { HqLayout } from "./components/hq/hq-layout.tsx";
import { lazyPage } from "./utils/lazy-page.tsx";

const AcceptInvitationPage = lazyPage(
  () => import("./app/auth/accept-invitation/page.tsx")
);
const PlaygroundPage = lazyPage(() => import("./app/playground/playground.tsx"));
const PresentPage = lazyPage(() => import("./app/present/page.tsx"));
const ForgotPasswordPage = lazyPage(
  () => import("./app/auth/forgot-password/page.tsx")
);
const LoginPage = lazyPage(() => import("./app/auth/login/page.tsx"));
const RegisterPage = lazyPage(() => import("./app/auth/register/page.tsx"));
const ResetPasswordPage = lazyPage(
  () => import("./app/auth/reset-password/page.tsx")
);
const DashboardPage = lazyPage(() => import("./app/dashboard/page.tsx"));
const ProductDemoDashboardPage = lazyPage(
  () => import("./app/product-demo/dashboard/page.tsx")
);
const ProductDemoMyBookingsPage = lazyPage(
  () => import("./app/product-demo/my/bookings/page.tsx")
);
const ProductDemoBookingPage = lazyPage(
  () => import("./app/product-demo/bookings/[bookingId]/page.tsx")
);
const ProductDemoMyPerformancePage = lazyPage(
  () => import("./app/product-demo/my/performance/page.tsx")
);
const ProductDemoTrialBalancePage = lazyPage(
  () => import("./app/product-demo/accounting/trial-balance/page.tsx")
);
const ProductDemoAccountingAutoJournalsPage = lazyPage(
  () => import("./app/product-demo/accounting/auto-journals/page.tsx")
);
const ProductDemoAccountingDailyTriagePage = lazyPage(
  () => import("./app/product-demo/accounting/daily-triage/page.tsx")
);
const ProductDemoAccountingImportBulkPayPage = lazyPage(
  () => import("./app/product-demo/accounting/import-bulk-pay/page.tsx")
);
const ProductDemoAccountingReconciliationPage = lazyPage(
  () => import("./app/product-demo/accounting/reconciliation/page.tsx")
);
const ProductDemoAccountingTradeExpensesPage = lazyPage(
  () => import("./app/product-demo/accounting/trade-expenses/page.tsx")
);
const ProductDemoAccountingMonthEndClosePage = lazyPage(
  () => import("./app/product-demo/accounting/month-end-close/page.tsx")
);
const ProductDemoQuoteCreatePage = lazyPage(
  () => import("./app/product-demo/quotes/create/page.tsx")
);
const ProductDemoQuoteSendPage = lazyPage(
  () => import("./app/product-demo/quotes/send/page.tsx")
);
const ProductDemoQuoteViewPage = lazyPage(
  () => import("./app/product-demo/quotes/view/page.tsx")
);
const ProductDemoQuoteAmendPage = lazyPage(
  () => import("./app/product-demo/quotes/amend/page.tsx")
);
const ProductDemoQuoteConfirmPage = lazyPage(
  () => import("./app/product-demo/quotes/confirm/page.tsx")
);
const ProductDemoQuoteCreateBookingPage = lazyPage(
  () => import("./app/product-demo/quotes/create-booking/page.tsx")
);
const ProductDemoQuoteEditorPage = lazyPage(
  () => import("./app/product-demo/quotes/editor/page.tsx")
);
const CustomerCreditVouchersPage = lazyPage(
  () => import("./app/customer-credit-vouchers/page.tsx")
);
const EnquiriesPage = lazyPage(() => import("./app/enquiries/page.tsx"));
const CustomerPage = lazyPage(() => import("./app/customers/page.tsx"));
const BookingsPage = lazyPage(() => import("./app/bookings/page.tsx"));
const BookingViewPage = lazyPage(
  () => import("./app/bookings/booking-view-page.tsx")
);
const PerformanceReportPage = lazyPage(
  () => import("./app/reports/performance/page.tsx")
);
const AtolLicensableBookingsPage = lazyPage(
  () => import("./app/reports/atol/licensable-bookings/page.tsx")
);
const AtolAraPackPage = lazyPage(
  () => import("./app/reports/atol/ara-pack/page.tsx")
);
const AtolApcPage = lazyPage(() => import("./app/reports/atol/apc/page.tsx"));
const ProfitAllocationReportPage = lazyPage(
  () => import("./app/accounting/profit-allocation/page.tsx")
);
const LedgerTransactionsPage = lazyPage(
  () => import("./app/ledger-transactions/page.tsx")
);
const AccountingInvoicesPage = lazyPage(
  () => import("./app/accounting/invoices/page.tsx")
);
const AccountingBillsPage = lazyPage(
  () => import("./app/accounting/bills/page.tsx")
);
const PDQReconciliationPage = lazyPage(
  () => import("./app/accounting/pdq-reconciliation/page.tsx")
);
const PdqReconciliationHubPage = lazyPage(
  () => import("./app/accounting/reconciliation/pdq/page.tsx")
);
const BulkPaySupplierBillsPage = lazyPage(
  () => import("./app/accounting/bulk-pay-supplier-bills/page.tsx")
);
const ImportCustomerReceiptsPage = lazyPage(
  () => import("./app/accounting/import-customer-receipts/page.tsx")
);
const ReconciliationPage = lazyPage(
  () => import("./app/accounting/reconciliation/page.tsx")
);
const BankReconciliationPage = lazyPage(
  () => import("./app/accounting/reconciliation/bank/page.tsx")
);
const BankReconciliationNewPage = lazyPage(
  () => import("./app/accounting/reconciliation/bank/new/page.tsx")
);
const BankReconciliationSessionPage = lazyPage(
  () => import("./app/accounting/reconciliation/bank/session-workspace-page.tsx")
);
const CardReconciliationPage = lazyPage(
  () => import("./app/accounting/reconciliation/card/page.tsx")
);
const CardReconciliationNewPage = lazyPage(
  () => import("./app/accounting/reconciliation/card/new/page.tsx")
);
const CardReconciliationSessionPage = lazyPage(
  () => import("./app/accounting/reconciliation/card/session-workspace-page.tsx")
);
const PdqReconciliationSessionPage = lazyPage(
  () => import("./app/accounting/reconciliation/pdq/session-workspace-page.tsx")
);
const SupplierReconciliationPage = lazyPage(
  () => import("./app/accounting/reconciliation/supplier/page.tsx")
);
const SupplierReconciliationNewPage = lazyPage(
  () => import("./app/accounting/reconciliation/supplier/new/page.tsx")
);
const SupplierReconciliationSessionPage = lazyPage(
  () => import("./app/accounting/reconciliation/supplier/session-workspace-page.tsx")
);
const ReconciliationTemplatesPage = lazyPage(
  () => import("./app/accounting/reconciliation/templates/page.tsx")
);
const AccountingCustomerInvoicePaymentsPage = lazyPage(
  () => import("./app/accounting/customer-invoice-payments/page.tsx")
);
const AccountingSupplierBillPaymentsPage = lazyPage(
  () => import("./app/accounting/supplier-bill-payments/page.tsx")
);
const TrialBalancePage = lazyPage(
  () => import("./app/accounting/trial-balance/page.tsx")
);
const ManualJournalsPage = lazyPage(
  () => import("./app/accounting/manual-journals/page.tsx")
);
const TradeExpensesPage = lazyPage(
  () => import("./app/finance/trade-expenses/page.tsx")
);
const AccountingGuidePage = lazyPage(
  () => import("./app/accounting/guide/page.tsx")
);
const GuidesIndexPage = lazyPage(() => import("./app/guides/page.tsx"));
const CreatingQuotesGuidePage = lazyPage(
  () => import("./app/guides/creating-quotes/page.tsx")
);
const BookingFlowGuidePage = lazyPage(
  () => import("./app/guides/booking-flow/page.tsx")
);
const OnboardingTravelAgenciesGuidePage = lazyPage(
  () => import("./app/guides/onboarding-travel-agencies/page.tsx")
);
const QuotesPage = lazyPage(() => import("./app/quotes/page.tsx"));
const QuoteTemplatesPage = lazyPage(
  () => import("./app/quote-templates/page.tsx")
);
const CampaignCreatorPage = lazyPage(
  () => import("./app/campaign-creator/page.tsx")
);
const HolidayQuoteDetailPage = lazyPage(
  () => import("./app/quotes/[quoteId]/page.tsx")
);
const MyCustomersPage = lazyPage(() => import("./app/my/customers/page.tsx"));
const MyEnquiriesPage = lazyPage(() => import("./app/my/enquiries/page.tsx"));
const MyQuotesPage = lazyPage(() => import("./app/my/quotes/page.tsx"));
const MyBookingsPage = lazyPage(() => import("./app/my/bookings/page.tsx"));
const MyPerformancePage = lazyPage(
  () => import("./app/my/performance/page.tsx")
);
const HqLoginPage = lazyPage(() => import("./app/hq/login/page.tsx"));
const HqOrganizationsPage = lazyPage(
  () => import("./app/hq/organizations/page.tsx")
);
const HqUsersPage = lazyPage(() => import("./app/hq/users/page.tsx"));
const HqMigrationsPage = lazyPage(() => import("./app/hq/migrations/page.tsx"));

const AuthenticatedRoutes = [
  {
    path: "/dashboard",
    component: DashboardPage,
    label: "Home",
  },
  {
    path: "/my/customers",
    component: MyCustomersPage,
    label: "My Customers",
  },
  {
    path: "/my/enquiries",
    component: MyEnquiriesPage,
    label: "My Enquiries",
  },
  {
    path: "/my/quotes",
    component: MyQuotesPage,
    label: "My Quotes",
  },
  {
    path: "/my/bookings",
    component: MyBookingsPage,
    label: "My Bookings",
  },
  {
    path: "/my/performance",
    component: MyPerformancePage,
    label: "My Performance",
  },
  {
    path: "/customers",
    component: CustomerPage,
    label: "All Customers",
  },
  {
    path: "/customer-credit-vouchers",
    component: CustomerCreditVouchersPage,
    label: "Customer Credit Vouchers",
  },
  {
    path: "/enquiries",
    component: EnquiriesPage,
    label: "All Enquiries",
  },
  {
    path: "/bookings/:id",
    component: BookingViewPage,
    label: "Booking",
  },
  {
    path: "/bookings",
    component: BookingsPage,
    label: "All Bookings",
  },
  {
    path: "/reports/performance",
    component: PerformanceReportPage,
    label: "Performance Report",
  },
  {
    path: "/reports/atol/licensable-bookings",
    component: AtolLicensableBookingsPage,
    label: "ATOL Licensable Bookings",
  },
  {
    path: "/reports/atol/ara-pack",
    component: AtolAraPackPage,
    label: "ATOL ARA Export Pack",
  },
  {
    path: "/reports/atol/apc",
    component: AtolApcPage,
    label: "ATOL APC Returns",
  },
  {
    path: "/accounting/profit-allocation",
    component: ProfitAllocationReportPage,
    label: "Profit Allocation Records",
  },
  {
    path: "/ledger-transactions",
    component: LedgerTransactionsPage,
    label: "Ledgers",
  },
  {
    path: "/accounting/guide",
    component: AccountingGuidePage,
    label: "How accounting works",
  },
  {
    path: "/guides/creating-quotes",
    component: CreatingQuotesGuidePage,
    label: "Creating quotes guide",
  },
  {
    path: "/guides/booking-flow",
    component: BookingFlowGuidePage,
    label: "Booking flow guide",
  },
  {
    path: "/guides/onboarding-travel-agencies",
    component: OnboardingTravelAgenciesGuidePage,
    label: "Onboarding travel agencies guide",
  },
  {
    path: "/guides",
    component: GuidesIndexPage,
    label: "Guides",
  },
  {
    path: "/accounting/manual-journals",
    component: ManualJournalsPage,
    label: "Manual Journals",
  },
  {
    path: "/accounting/trial-balance",
    component: TrialBalancePage,
    label: "Trial Balance",
  },
  {
    path: "/finance/trade-expenses",
    component: TradeExpensesPage,
    label: "Trade expenses",
  },
  {
    path: "/accounting/invoices",
    component: AccountingInvoicesPage,
    label: "Accounting invoices",
  },
  {
    path: "/accounting/bills",
    component: AccountingBillsPage,
    label: "Accounting bills",
  },
  {
    path: "/accounting/pdq-reconciliation",
    component: PDQReconciliationPage,
    label: "PDQ Reconciliation",
  },
  {
    path: "/accounting/reconciliation/pdq/:sessionId",
    component: PdqReconciliationSessionPage,
    label: "PDQ charges session",
  },
  {
    path: "/accounting/reconciliation/pdq",
    component: PdqReconciliationHubPage,
    label: "PDQ charges",
  },
  {
    path: "/accounting/reconciliation",
    component: ReconciliationPage,
    label: "Reconciliation",
  },
  {
    path: "/accounting/reconciliation/bank/new",
    component: BankReconciliationNewPage,
    label: "New bank reconciliation",
  },
  {
    path: "/accounting/reconciliation/bank/:sessionId",
    component: BankReconciliationSessionPage,
    label: "Bank reconciliation session",
  },
  {
    path: "/accounting/reconciliation/bank",
    component: BankReconciliationPage,
    label: "Bank reconciliation",
  },
  {
    path: "/accounting/reconciliation/card/new",
    component: CardReconciliationNewPage,
    label: "Import card settlement",
  },
  {
    path: "/accounting/reconciliation/card/:sessionId",
    component: CardReconciliationSessionPage,
    label: "Card settlement session",
  },
  {
    path: "/accounting/reconciliation/card",
    component: CardReconciliationPage,
    label: "Card settlement",
  },
  {
    path: "/accounting/reconciliation/supplier/new",
    component: SupplierReconciliationNewPage,
    label: "Import supplier statement",
  },
  {
    path: "/accounting/reconciliation/supplier/:sessionId",
    component: SupplierReconciliationSessionPage,
    label: "Supplier reconciliation session",
  },
  {
    path: "/accounting/reconciliation/supplier",
    component: SupplierReconciliationPage,
    label: "Supplier reconciliation",
  },
  {
    path: "/accounting/reconciliation/templates",
    component: ReconciliationTemplatesPage,
    label: "Reconciliation templates",
  },
  {
    path: "/accounting/bulk-pay-supplier-bills",
    component: BulkPaySupplierBillsPage,
    label: "Bulk Pay Supplier Bills",
  },
  {
    path: "/accounting/import-customer-receipts",
    component: ImportCustomerReceiptsPage,
    label: "Import Customer Receipts",
  },
  {
    path: "/accounting/customer-invoice-payments",
    component: AccountingCustomerInvoicePaymentsPage,
    label: "Customer Invoice Payments",
  },
  {
    path: "/accounting/supplier-bill-payments",
    component: AccountingSupplierBillPaymentsPage,
    label: "Supplier Bill Payments",
  },
  {
    path: "/quotes/:quoteId",
    component: HolidayQuoteDetailPage,
    label: "Quote",
  },
  {
    path: "/quotes",
    component: QuotesPage,
    label: "All Quotes",
  },
  {
    path: "/quote-templates",
    component: QuoteTemplatesPage,
    label: "Quote Templates",
  },
  {
    path: "/campaign-creator",
    component: CampaignCreatorPage,
    label: "Campaign Creator",
  },
  {
    path: "/playground",
    component: PlaygroundPage,
  },
  {
    path: "/product-demo/dashboard",
    component: ProductDemoDashboardPage,
  },
  {
    path: "/product-demo/my/bookings",
    component: ProductDemoMyBookingsPage,
  },
  {
    path: "/product-demo/bookings/:bookingId",
    component: ProductDemoBookingPage,
  },
  {
    path: "/product-demo/my/performance",
    component: ProductDemoMyPerformancePage,
  },
  {
    path: "/product-demo/accounting/trial-balance",
    component: ProductDemoTrialBalancePage,
  },
  {
    path: "/product-demo/accounting/auto-journals",
    component: ProductDemoAccountingAutoJournalsPage,
  },
  {
    path: "/product-demo/accounting/daily-triage",
    component: ProductDemoAccountingDailyTriagePage,
  },
  {
    path: "/product-demo/accounting/import-bulk-pay",
    component: ProductDemoAccountingImportBulkPayPage,
  },
  {
    path: "/product-demo/accounting/reconciliation",
    component: ProductDemoAccountingReconciliationPage,
  },
  {
    path: "/product-demo/accounting/trade-expenses",
    component: ProductDemoAccountingTradeExpensesPage,
  },
  {
    path: "/product-demo/accounting/month-end-close",
    component: ProductDemoAccountingMonthEndClosePage,
  },
  {
    path: "/product-demo/quotes/create",
    component: ProductDemoQuoteCreatePage,
  },
  {
    path: "/product-demo/quotes/send",
    component: ProductDemoQuoteSendPage,
  },
  {
    path: "/product-demo/quotes/view",
    component: ProductDemoQuoteViewPage,
  },
  {
    path: "/product-demo/quotes/amend",
    component: ProductDemoQuoteAmendPage,
  },
  {
    path: "/product-demo/quotes/confirm",
    component: ProductDemoQuoteConfirmPage,
  },
  {
    path: "/product-demo/quotes/create-booking",
    component: ProductDemoQuoteCreateBookingPage,
  },
  {
    path: "/product-demo/quotes/editor",
    component: ProductDemoQuoteEditorPage,
  },
];

const UnauthenticatedRoutes = [
  {
    path: "/login",
    component: LoginPage,
  },
  {
    path: "/forgot-password",
    component: ForgotPasswordPage,
  },
  {
    path: "/reset-password",
    component: ResetPasswordPage,
  },
  {
    path: "/register",
    component: RegisterPage,
  },
  {
    path: "/accept-invitation",
    component: AcceptInvitationPage,
  },
  {
    path: "/playground",
    component: PlaygroundPage,
  },
];

export const MainRouter = () => {
  const isAuthenticated = useSelector(selectAccessToken);

  const specialDefaultPage = useMemo(() => {
    const isRegisterDomain = window.location.host.split(".")[0] === "register";
    const isHQDomain = window.location.host.split(".")[0] === "hq";
    const isRoadmapDomain = window.location.host.split(".")[0] === "roadmap";
    const isLoginDomain =
      window.location.host.split(".")[0] === "login" ||
      window.location.host.split(".")[0] === "clickconnector";
    if (isRegisterDomain) {
      return "/register";
    }
    if (isHQDomain) {
      return `/hq/login`;
    }
    if (isRoadmapDomain) {
      return `/roadmap`;
    }
    if (isLoginDomain) {
      return `/signin`;
    }
    return `/login`;
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<App />}>
          <Route path="/present" Component={PresentPage} />
          <Route path="/hq/login" Component={HqLoginPage} />
          <Route element={<HqAuthGuard />}>
            <Route element={<HqLayout />}>
              <Route
                path="/hq"
                Component={() => <Redirect to="/hq/organizations" />}
              />
              <Route path="/hq/organizations" Component={HqOrganizationsPage} />
              <Route path="/hq/users" Component={HqUsersPage} />
              <Route path="/hq/migrations" Component={HqMigrationsPage} />
            </Route>
          </Route>

          {isAuthenticated && (
            <>
              <Route element={<RootLayout />}>
                <Route element={<RouteGuard />}>
                  {AuthenticatedRoutes.map((item) => (
                    <Route
                      path={item.path}
                      Component={item.component}
                      key={item.path}
                    ></Route>
                  ))}
                </Route>
              </Route>
              <Route
                path="*"
                Component={() => <Redirect to={`/dashboard`} />}
              />
            </>
          )}

          {!isAuthenticated && (
            <>
              <Route element={<AuthLayout />}>
                {UnauthenticatedRoutes.map((item) => (
                  <Route
                    path={item.path}
                    Component={item.component}
                    key={item.path}
                  ></Route>
                ))}
              </Route>
              <Route
                path="*"
                Component={() => <Redirect to={`${specialDefaultPage}`} />}
              />
            </>
          )}
        </Route>
      </Routes>
      <AppModalRegistry />
    </BrowserRouter>
  );
};

const Redirect = ({ to }: { to: string }) => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate(to);
  }, [navigate, to]);
  return <></>;
};
